/**
 * Memory retrieval (AgentCore-inspired)
 *
 * Rank and format durable memories for prompt injection using topK +
 * relevance scoring, grouped by strategy namespace.
 */

import {
  MEMORY_RETRIEVAL_DEFAULTS,
  memoryStrategyLabel,
  normalizeMemoryStrategy,
  type MemoryStrategy
} from '@/lib/memory/strategies'
import { tokenizeSearchQuery } from '@/lib/search/tokenize-query'

export type RetrievableMemory = {
  id?: string
  content: string
  memory_type?: string | null
  confidence?: number | null
  created_at?: string | null
  last_referenced?: string | null
}

export type RankedMemory = RetrievableMemory & {
  strategy: MemoryStrategy
  score: number
}

export type RetrieveMemoriesOptions = {
  query?: string | null
  topK?: number
  minRelevanceScore?: number
}

// --------------

function tokenize(value: string) {
  return tokenizeSearchQuery(value.toLowerCase())
}

// --------------

function scoreMemory(
  memory: RetrievableMemory,
  queryTokens: string[],
  queryLower: string | null
): number {
  const content = (memory.content || '').trim()
  if (!content) return 0

  const contentLower = content.toLowerCase()
  const confidence =
    typeof memory.confidence === 'number' && Number.isFinite(memory.confidence)
      ? Math.max(0, Math.min(1, memory.confidence))
      : 0.5

  let score = 0.15 + confidence * 0.35

  if (queryLower) {
    if (contentLower.includes(queryLower)) {
      score += 0.45
    }

    if (queryTokens.length > 0) {
      const hits = queryTokens.filter(token => contentLower.includes(token)).length
      score += (hits / queryTokens.length) * 0.4
    }
  } else {
    // No query: prefer higher confidence and fresher / recently referenced.
    const stamp = memory.last_referenced || memory.created_at
    if (stamp) {
      const ageMs = Date.now() - Date.parse(stamp)
      if (Number.isFinite(ageMs) && ageMs >= 0) {
        const ageDays = ageMs / (1000 * 60 * 60 * 24)
        score += Math.max(0, 0.2 - ageDays * 0.01)
      }
    }
  }

  const strategy = normalizeMemoryStrategy(memory.memory_type)
  if (strategy === 'preference') score += 0.05
  if (strategy === 'summary' && !queryLower) score += 0.03

  return Math.max(0, Math.min(1, score))
}

// --------------

export function rankMemoriesForPrompt(
  memories: RetrievableMemory[],
  options: RetrieveMemoriesOptions = {}
): RankedMemory[] {
  const topK = options.topK ?? MEMORY_RETRIEVAL_DEFAULTS.topK
  const minScore =
    options.minRelevanceScore ?? MEMORY_RETRIEVAL_DEFAULTS.minRelevanceScore
  const query =
    typeof options.query === 'string' && options.query.trim()
      ? options.query.trim()
      : null
  const queryLower = query?.toLowerCase() ?? null
  const queryTokens = queryLower ? tokenize(queryLower) : []

  return memories
    .map(memory => {
      const strategy = normalizeMemoryStrategy(memory.memory_type)
      const score = scoreMemory(memory, queryTokens, queryLower)
      return { ...memory, strategy, score }
    })
    .filter(memory => memory.content.trim().length > 0 && memory.score >= minScore)
    .sort((left, right) => {
      if (right.score !== left.score) return right.score - left.score
      return right.content.length - left.content.length
    })
    .slice(0, Math.max(1, topK))
}

// --------------

export function formatRankedMemoriesForPrompt(ranked: RankedMemory[]): string {
  if (ranked.length === 0) return ''

  const byStrategy = new Map<MemoryStrategy, RankedMemory[]>()
  for (const memory of ranked) {
    const list = byStrategy.get(memory.strategy) ?? []
    list.push(memory)
    byStrategy.set(memory.strategy, list)
  }

  const order: MemoryStrategy[] = [
    'preference',
    'semantic',
    'summary',
    'episodic'
  ]
  const lines: string[] = [
    'Long-term memories (ranked for this turn; nightly AssistantMemory may add more):'
  ]

  for (const strategy of order) {
    const items = byStrategy.get(strategy)
    if (!items?.length) continue
    lines.push(`${memoryStrategyLabel(strategy)}:`)
    for (const item of items) {
      const conf =
        typeof item.confidence === 'number' && Number.isFinite(item.confidence)
          ? ` [${Math.round(item.confidence * 100)}%]`
          : ''
      lines.push(`- ${item.content.trim()}${conf}`)
    }
  }

  return lines.join('\n')
}

// --------------

export function retrieveMemoriesForPrompt(
  memories: RetrievableMemory[],
  options: RetrieveMemoriesOptions = {}
): string {
  return formatRankedMemoriesForPrompt(rankMemoriesForPrompt(memories, options))
}
